// Single shared source of truth for GET /api/auth/user.
// authSlice.js (validateToken/buildUserData) and userDetailsSlice.js
// (fetchUserDetails) both independently called this endpoint before -
// this module gives them one shared cache instead of two separate ones,
// so the browser only makes one real request per cache window regardless
// of how many callers ask for it.
import axios from "axios";
import { getAuthToken } from "./getAuthToken";

const BASE_URL = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
const CACHE_DURATION = 10000; // 10 seconds - matches userDetailsSlice's existing cache window

let cached = null; // { timestamp, promise }

export const getUserData = async ({ force = false } = {}) => {
  const now = Date.now();
  if (!force && cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.promise;
  }

  const promise = (async () => {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("No token found");
    }
    const response = await axios.get(`${BASE_URL}/api/auth/user`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  })();

  cached = { timestamp: now, promise };

  // Don't let a failed fetch poison the cache for the full TTL - clear it
  // immediately on rejection so the next caller (e.g. authSlice's
  // retry-after-token-refresh path) gets a fresh attempt instead of the
  // same stale rejection tied to the token that just failed.
  promise.catch(() => {
    if (cached?.promise === promise) {
      cached = null;
    }
  });

  return promise;
};

// Call on logout so the next account's first fetch on this tab isn't
// served the previous account's cached data.
export const __resetUserDataSourceCache = () => {
  cached = null;
};
